/**
 * 차트 데이터를 pivot 형태로 변환하는 유틸리티 함수
 */
export interface RawChartData {
  companyName: string;
  month: string;
  monthlyAmount: number;
}

export interface ChartDataPoint {
  month: string;
  [companyName: string]: string | number;
}

/**
 * 월 포맷팅 (2025-01 -> 2025년 1월)
 */
function formatMonth(month: string): string {
  const [year, monthNum] = month.split("-");
  return `${year}년 ${parseInt(monthNum)}월`;
}

/**
 * raw 데이터를 차트용 pivot 형태로 변환
 * @param rawData - API에서 받아온 원시 데이터
 * @returns 차트에 사용할 수 있는 pivot 형태 데이터
 */
export function transformChartData(rawData: RawChartData[]): ChartDataPoint[] {
  if (!rawData || rawData.length === 0) return [];

  // 모든 고유한 월 추출
  const uniqueMonths = [...new Set(rawData.map(item => item.month))].sort();

  // 모든 고유한 회사명 추출
  const uniqueCompanies = [...new Set(rawData.map(item => item.companyName))];

  // 월별로 그룹화하여 pivot
  return uniqueMonths.map(month => {
    const row: ChartDataPoint = { month: formatMonth(month) };

    // 해당 월의 모든 회사 데이터 추가
    uniqueCompanies.forEach(company => {
      const dataPoint = rawData.find(item => item.companyName === company && item.month === month);
      row[company] = dataPoint ? dataPoint.monthlyAmount : 0;
    });

    return row;
  });
}

/**
 * 회사 정보를 생성
 * @param rawData - API에서 받아온 원시 데이터
 * @param colorPalette - 사용할 색상 팔레트 (선택)
 * @returns 회사 설정 배열
 */
export interface CompanyConfig {
  name: string;
  dataKey: string;
  color: string;
}

export function generateCompanyConfig(
  rawData: RawChartData[],
  colorPalette: string[] = ["#52C41A", "#FA8C16", "#1890FF", "#FADB14", "#CF1322", "#722ED1", "#EB2F96", "#13C2C2"]
): CompanyConfig[] {
  if (!rawData || rawData.length === 0) return [];

  const uniqueCompanies = [...new Set(rawData.map(item => item.companyName))];

  return uniqueCompanies.map((company, index) => ({
    name: company,
    dataKey: company,
    color: colorPalette[index % colorPalette.length],
  }));
}

/**
 * 차트 Y축 도메인 계산
 * @param rawData - 원시 데이터
 * @param padding - 상단 여백 (기본 0.1 = 10%)
 * @returns [min, max] 튜플
 */
export function calculateDomain(rawData: RawChartData[], padding: number = 0.1): [number, number] {
  if (!rawData || rawData.length === 0) return [0, 100];

  const amounts = rawData.map(item => item.monthlyAmount);
  const max = Math.max(...amounts);
  const min = Math.min(...amounts);
  const range = max - min;

  return [Math.max(0, min - range * padding), max + range * padding];
}

/**
 * Y축 틱 값 생성 (다섯 개의 간격으로)
 * @param min - 최소값
 * @param max - 최대값
 * @returns 틱 배열
 */
export function generateYAxisTicks(min: number, max: number): number[] {
  const ticks: number[] = [];
  const range = max - min;
  const interval = range / 4; // 5개의 틱 (시작점 + 4개 간격)

  for (let i = 0; i <= 4; i++) {
    ticks.push(Math.floor(min + interval * i));
  }

  return ticks;
}
